import { HubsAPI, type CreateHubRequest } from '@/backend/hubs'
import { updateProject } from '@/backend/updateProject'
import { fakeUnwrap, isAllAvailable, type Eventually } from '@/data/clientUtils'
import { hubNameToProjectName } from '@/data/Hubs/hubName'
import type { Hub, RawHub } from '@/data/Hubs/types'
import { useHubStore } from '@/data/Hubs/useHubStore'
import { wsTopic } from '@/data/websocketTopics'
import { queryClient } from '@/modules/App/queryClient'
import { serializeProjectInList } from '@/modules/Projects/serializers'
import { invariant, nonNullable } from '@/shared/utils/typeAssertions'
import { useWebSocketTopic } from '@/sharedComposables/useWebSocketTopic'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { camelCase } from 'change-case/keys'
import { computed, toValue } from 'vue'

export function useHubClient(props: { workspaceId: Eventually<string> }) {
  const hubStore = useHubStore()
  const workspaceTopic = computed(() => wsTopic.workspace(props.workspaceId))

  /**
   * Fetches all hubs for the current workspace
   */
  function fetchAll() {
    subscribeToWorkspaceUpdates()

    return useQuery({
      queryKey: [props.workspaceId, 'hubs'],
      staleTime: 10_000,
      enabled: isAllAvailable(() => props.workspaceId),
      queryFn: async () => {
        const workspaceId = toValue(props.workspaceId)
        invariant(workspaceId)
        const result = await HubsAPI.listHubs(workspaceId)
        if (!result.ok) throw new Error(result.error.message)

        hubStore.hubs.clear()
        upsert(...result.data.data)
        return hubStore.hubs
      },
    })
  }

  function fetch(hubId: Eventually<string>) {
    subscribeToWorkspaceUpdates()

    return useQuery({
      queryKey: [props.workspaceId, 'hub', hubId],
      enabled: isAllAvailable([() => props.workspaceId, () => hubId]),
      staleTime: 10_000,
      select: (data) => fakeUnwrap(data),
      queryFn: async () => {
        const workspaceId = toValue(props.workspaceId)
        const hubIdValue = nonNullable(toValue(hubId))
        invariant(workspaceId)
        const result = await HubsAPI.getHub(workspaceId, hubIdValue)
        if (!result.ok) throw new Error(result.error.message)
        upsert(result.data)

        return computed(() => hubStore.hubs.get(hubIdValue))
      },
    })
  }

  function upsert(...hubList: RawHub[]) {
    for (const rawHub of hubList) {
      hubStore.hubs.set(rawHub.id, camelCase(rawHub, Infinity) as Hub)
    }
  }

  function createMutation() {
    return useMutation({
      mutationFn: async (req: CreateHubRequest) => {
        const workspaceId = toValue(props.workspaceId)
        invariant(workspaceId)
        const response = await HubsAPI.createHub(workspaceId, req)
        if (!response.ok) throw new Error(response.error.message)
        upsert(response.data)
        return nonNullable(hubStore.hubs.get(response.data.id))
      },
      onSettled() {
        return queryClient.invalidateQueries({ queryKey: [props.workspaceId, 'hubs'] })
      },
    })
  }

  function updateMutation() {
    return useMutation({
      mutationFn: async (params: { hubId: string; name: string; description: string }) => {
        const payload = {
          name: hubNameToProjectName(params.name),
          description: params.description,
        }

        const workspaceId = toValue(props.workspaceId)
        invariant(workspaceId)
        const response = await updateProject(workspaceId, params.hubId, payload)

        if (!response.ok) throw new Error(response.error.message)

        const hub = hubStore.hubs.get(params.hubId)
        invariant(hub, 'Hub not found')
        if (hub) {
          hub.name = params.name
          hub.description = params.description
        }
        return hub
      },
      onSettled(_response, _error, request) {
        const hubInvalidation = queryClient.invalidateQueries({
          queryKey: [props.workspaceId, 'hub', request.hubId],
        })
        const listInvalidation = queryClient.invalidateQueries({
          queryKey: [props.workspaceId, 'hubs'],
        })
        return Promise.all([hubInvalidation, listInvalidation])
      },
    })
  }

  function deleteMutation() {
    return useMutation({
      mutationFn: async (hubId: string) => {
        const workspaceId = toValue(props.workspaceId)
        invariant(workspaceId)
        const response = await HubsAPI.deleteHub(workspaceId, hubId)
        if (!response.ok) throw new Error(response.error.message)
      },
      onMutate(id) {
        hubStore.hubs.delete(id)
      },
      onError() {
        return queryClient.invalidateQueries({ queryKey: [props.workspaceId, 'hubs'] })
      },
    })
  }

  function subscribeToWorkspaceUpdates() {
    const { subscribe } = useWebSocketTopic(workspaceTopic)

    subscribe('project:updated', (data) => {
      hubStore.patch(serializeProjectInList(data))
    })
  }

  return {
    createMutation,
    updateMutation,
    deleteMutation,
    fetch,
    fetchAll,
  }
}
